import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'

// FIX: Force HMR updates to apply even when tab is in background
// Browsers throttle JS execution for background tabs, causing HMR to queue updates
// This tracks pending updates and triggers reload when tab becomes visible
if (import.meta.hot) {
  let pendingHMRUpdates = 0

  import.meta.hot.on('vite:beforeUpdate', () => {
    pendingHMRUpdates++
  })

  import.meta.hot.on('vite:afterUpdate', () => {
    pendingHMRUpdates = Math.max(0, pendingHMRUpdates - 1)
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && pendingHMRUpdates > 0) {
      window.location.reload()
    }
  })
}

import './index.css'
import './i18n/config'
import { setupGlobalErrorHandlers } from './services/errorReporting'
import { logStartupConfig } from './config/startupLog'
import { installConsoleCapture } from './features/bug-report/consoleCapture'
import App from './App.tsx'
import { BugReportCaptureProvider } from './features/bug-report/BugReportCaptureProvider'
import { NotificationsProvider } from './features/notifications/useNotifications'

// Console capture installed early; enabled at runtime when tenant config loads
installConsoleCapture()

// Initialize global error handlers EARLY to catch errors during React initialization
// This sends frontend errors to backend telemetry (Azure App Insights + ELK)
setupGlobalErrorHandlers()

logStartupConfig()

import { ToastProvider } from './components/Toast'
import { ThemeProvider } from './contexts/ThemeContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { AuthProvider } from './contexts/AuthContext'
import { AuthConfigProvider } from './contexts/AuthConfigContext'
import { UiCustomizationProvider } from './contexts/UiCustomizationContext'
import { TenantProvider } from './contexts/TenantContext'
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext'
import { AppModeProvider } from './contexts/AppModeContext'
import { AssumedRoleProvider } from './contexts/AssumedRoleContext'
import { QueryProvider } from './providers/QueryProvider'
import { configureMonacoLoader } from './monaco/configureMonacoLoader'
import { initPosthog } from './services/posthog'

configureMonacoLoader()
initPosthog()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryProvider>
        <ToastProvider>
          {/* UiCustomizationProvider: fetches pre-login branding + dropdowns */}
          <UiCustomizationProvider>
          {/* AuthConfigProvider must be before AuthProvider so config is available */}
          <AuthConfigProvider>
            <AuthProvider>
              <TenantProvider>
                <FeatureFlagsProvider>
                  <AppModeProvider>
                    <AssumedRoleProvider>
                      <LanguageProvider>
                        <ThemeProvider>
                          <BugReportCaptureProvider>
                            <NotificationsProvider>
                              <App />
                            </NotificationsProvider>
                          </BugReportCaptureProvider>
                        </ThemeProvider>
                      </LanguageProvider>
                    </AssumedRoleProvider>
                  </AppModeProvider>
                </FeatureFlagsProvider>
              </TenantProvider>
            </AuthProvider>
          </AuthConfigProvider>
          </UiCustomizationProvider>
        </ToastProvider>
      </QueryProvider>
    </BrowserRouter>
  </StrictMode>
)
